<template>
	<NuxtLayout name="two-column-layout">
		<template #title>{{ $t(`homepageHeading_checkout`) }}</template>

		<template #subtitle>{{ $t(`homepageBody_checkout`) }}</template>

		<template #contentSlot1>
			<StepperWidget v-if="!showRestrictedCheckoutPage" />
		</template>

		<template #contentSlot2>
			<Transition name="slide-fade">
				<div
					v-show="renderCheckoutFields"
					class="grid gap-4 checkoutCardLeft">
					<CheckoutShippingInformation ref="shippingInformation" />

					<CheckoutBillingInformation ref="billingInformation" />

					<CheckoutInstallmentsCard />

					<CheckoutRefundableBooking />

					<CheckoutPaymentMethodDisabled
						ref="paymentInformation"
						v-if="shouldDisableCheckout" />
					<CheckoutPaymentMethodPaysafe
						ref="paymentInformation"
						v-else />
				</div>
			</Transition>
		</template>

		<template #contentSlot3>
			<Transition name="slide-fade">
				<div
					v-show="renderCheckoutFields"
					class="md:h-full relative checkoutCardRight">
					<div class="md:sticky md:top-5">
						<CheckoutEmptyCart v-if="showEmptyCart" />
						<CheckoutOrderSummary v-else />

						<MarketingTrademark />
					</div>
				</div>
			</Transition>

			<CheckoutTermsAndConditionsPopup
				ref="termsAndConditionsPopupRef"
				@declined="declinedTerms"
				@accepted="acceptedTerms" />
		</template>
	</NuxtLayout>
</template>

<script setup>
	import { useI18n } from "vue-i18n"
	import { useCartStore } from "~/stores/CartStore"
	import { usePaysafeStore } from "~/stores/PaysafeStore"
	import { useRefsStore } from "~/stores/RefsStore"
	import { useSettingsStore } from "~/stores/SettingsStore"
	import { onMounted, onUnmounted } from "vue"
	import { CHECKOUT_SUBMIT, ENABLE_PLACE_ORDER, PAYSAFE_TOKENIZE, PAYSAFE_TOKENIZED } from "~/assets/events/checkout.js"
	import { SHOW_ERROR } from "assets/events/errors.js"
	import { SHOW_ORDER_PLACED_POPUP } from "assets/events/checkout.js"
	import { PAYSAFE } from "assets/constants/paymentProviders.js"
	import { storeToRefs } from "pinia"

	const { $eventBus, $cartService, $seatSelector, $termsService, $configs } = useNuxtApp()
	const { t } = useI18n()
	const runtimeConfig = useRuntimeConfig()
	const isCheckoutDisabled = runtimeConfig.public.IS_CHECKOUT_DISABLED
	const shouldDisableCheckout = $configs.$isConfigEnabled(isCheckoutDisabled)
	const cartStore = useCartStore()
	const paysafeStore = usePaysafeStore()
	const refsStore = useRefsStore()
	const settingsStore = useSettingsStore()
	const PAYMENT_PROVIDER = runtimeConfig.public.PAYMENT_PROVIDER
	const { showRestrictedCheckoutPage } = storeToRefs(settingsStore)

	const shippingInformation = ref(null)
	const paymentInformation = ref(null)
	const billingInformation = ref(null)
	const renderCheckoutFields = ref(false)
	const termsAndConditionsPopupRef = ref(null)

	//Controls whether to render the empty cart page or not
	const showEmptyCart = computed(() => cartStore.getTotalItemsCount() === 0)

	//Set the Page Metadata
	useHead(() => {
		return {
			title: t("checkout"),
			meta: [{ name: "description", content: `Complete your order for ${runtimeConfig.public.EVENT_NAME}` }],
		}
	})

	const showValidationError = () => {
		const topMostValidationError = document.querySelectorAll(".inputFieldError")[0]
		if (topMostValidationError) {
			topMostValidationError.closest(".inputFieldContainer").scrollIntoView({ behavior: "smooth" })

			//subtract some pixels for the footer
			const bottomOfViewport = window.innerHeight - 100
			const checkPosOfErrorBeforeWiggle = () => {
				if (!topMostValidationError) {
					//User may have navigated away from the checkout page before the animation frame completed
					return
				}
				const newPos = topMostValidationError.getBoundingClientRect().top
				const isElementInViewport = newPos > 0 && newPos < bottomOfViewport
				if (isElementInViewport) {
					//Wiggle all the fields with errors
					document
						.querySelectorAll(".inputFieldError")
						.forEach((el) => el.parentElement.classList.add("animate-wiggle"))
					//Reset the wiggle animation
					setTimeout(() => {
						document.querySelectorAll(".inputFieldError").forEach((el) => {
							el.parentElement.classList.remove("animate-wiggle")
						})
					}, 1000)
				} else {
					requestAnimationFrame(checkPosOfErrorBeforeWiggle)
				}
			}

			requestAnimationFrame(checkPosOfErrorBeforeWiggle)
		}
	}

	const validateForm = () => {
		if (!refsStore.shippingInformationRef || !refsStore.paymentInformationRef || !refsStore.billingInformationRef) {
			$eventBus.$emit(SHOW_ERROR, {
				titleText: t("errorPageTitle"),
				bodyText: t("errorPopupRefresh"),
				closeBtnText: t("refresh"),
				closeCallback: reloadNuxtApp,
			})
			return
		}

		if (!validateIfCartNotEmpty()) {
			return
		}

		if (!validateAndRevealInvalidFields()) {
			return
		}

		termsAndConditionsPopupRef.value.openModal()
	}

	const validateIfCartNotEmpty = () => {
		if (!cartStore.cartId || !cartStore.items.size) {
			$eventBus.$emit(SHOW_ERROR, {
				titleText: t("noCartItemsTitle"),
				bodyText: t("noCartItemsForOrder"),
				clickOutsideEnabled: true,
			})
			$eventBus.$emit(ENABLE_PLACE_ORDER)
			return false
		}
		return true
	}

	const validateAndRevealInvalidFields = () => {
		let checkoutFieldsAreValid = true
		checkoutFieldsAreValid &= refsStore.shippingInformationRef.isShippingInfoValid()
		checkoutFieldsAreValid &= refsStore.paymentInformationRef.isPaymentInfoValid()
		checkoutFieldsAreValid &= refsStore.billingInformationRef.isBillingInfoValid()

		if (!checkoutFieldsAreValid) {
			//Scroll the user to the top-most invalid field
			setTimeout(() => {
				try {
					$eventBus.$emit(ENABLE_PLACE_ORDER)
					showValidationError()
				} catch (e) {
					$eventBus.$emit(SHOW_ERROR, {
						titleText: t("errorPageTitle"),
						bodyText: t("checkoutPageError"),
						closeBtnText: t("ok"),
					})
				}
			}, 100)
		}

		return checkoutFieldsAreValid
	}

	const declinedTerms = () => {
		$eventBus.$emit(ENABLE_PLACE_ORDER)
	}

	const acceptedTerms = ({ captchaToken }) => {
		cartStore.setCaptchaToken(captchaToken)

		if (PAYMENT_PROVIDER === PAYSAFE) {
			$eventBus.$emit(PAYSAFE_TOKENIZE)
		}
	}

	const placeOrder = () => {
		$cartService
			.$placeOrder(() => {
				$cartService.$resetStoredCartData()
				$eventBus.$emit(SHOW_ORDER_PLACED_POPUP)

				if (runtimeConfig.public.ENABLE_SIGNATURE_BOX) {
					$termsService.$storeTerms()
				}
				//At this point the order placed popup will appear. The confirmation page was failing to load for about 1% of users. So now the popup will redirect the user to the confirmation page.
				// navigateTo({ path: "/order/confirmation" })
			})
			.finally(() => {
				//Try to enable the button immediately, otherwise enable it after the page has rendered
				$eventBus.$emit(ENABLE_PLACE_ORDER)
				setTimeout(() => {
					$eventBus.$emit(ENABLE_PLACE_ORDER)
				}, 1000)
			})
	}

	onMounted(() => {
		$eventBus.$emit(ENABLE_PLACE_ORDER)

		//Fix the broken refs when navigating away
		refsStore.setShippingInformationRef(shippingInformation.value)
		refsStore.setPaymentInformationRef(paymentInformation.value)
		refsStore.setBillingInformationRef(billingInformation.value)

		//Add event listener for checkout
		$eventBus.$on(CHECKOUT_SUBMIT, validateForm)

		//Add event listener for paysafe tokenized, this will place the order
		if (PAYMENT_PROVIDER === PAYSAFE) {
			$eventBus.$on(PAYSAFE_TOKENIZED, placeOrder)
		}

		nextTick(() => {
			renderCheckoutFields.value = true
			// termsAndConditionsPopupRef.value.openModal()
		})
	})

	onUnmounted(() => {
		//Remove event listener for checkout
		$eventBus.$off(CHECKOUT_SUBMIT)

		//Add event listener for paysafe tokenized, this will place the order
		if (PAYMENT_PROVIDER === PAYSAFE) {
			$eventBus.$off(PAYSAFE_TOKENIZED)
		}
	})
</script>

<style lang="scss" scoped></style>
